'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import TeamLogo from '@/components/ui/TeamLogo';
import ConfidenceBadge from '@/components/ui/ConfidenceBadge';
import dynamic from 'next/dynamic';

const AccuracyTimelineChart = dynamic(() => import('./AccuracyTimelineChart'), { ssr: false });

interface Projection {
  id: string;
  date: string;
  homeTeam: { name: string; abbr: string; color: string };
  awayTeam: { name: string; abbr: string; color: string };
  predictedWinner: string;
  actualWinner: string | null;
  confidence: number;
  homeWinProb: number;
  awayWinProb: number;
  projectedScore: string;
  actualScore: string | null;
  correct: boolean | null;
  homeXG: number;
  awayXG: number;
}

const PROJECTIONS: Projection[] = [
  { id: 'proj-001', date: '15 mai 2026', homeTeam: { name: 'Boston Bruins', abbr: 'BOS', color: '#FCB514' }, awayTeam: { name: 'Tampa Bay Lightning', abbr: 'TBL', color: '#002868' }, predictedWinner: 'BOS', actualWinner: 'BOS', confidence: 73, homeWinProb: 63, awayWinProb: 37, projectedScore: '3–2', actualScore: '4–1', correct: true, homeXG: 3.44, awayXG: 2.81 },
  { id: 'proj-002', date: '15 mai 2026', homeTeam: { name: 'Edmonton Oilers', abbr: 'EDM', color: '#FF4C00' }, awayTeam: { name: 'Minnesota Wild', abbr: 'MIN', color: '#154734' }, predictedWinner: 'EDM', actualWinner: 'EDM', confidence: 81, homeWinProb: 72, awayWinProb: 28, projectedScore: '4–2', actualScore: '5–2', correct: true, homeXG: 3.88, awayXG: 2.74 },
  { id: 'proj-003', date: '15 mai 2026', homeTeam: { name: 'Colorado Avalanche', abbr: 'COL', color: '#6F263D' }, awayTeam: { name: 'Nashville Predators', abbr: 'NSH', color: '#FFB81C' }, predictedWinner: 'COL', actualWinner: 'NSH', confidence: 64, homeWinProb: 59, awayWinProb: 41, projectedScore: '3–2', actualScore: '2–3', correct: false, homeXG: 3.55, awayXG: 2.91 },
  { id: 'proj-004', date: '14 mai 2026', homeTeam: { name: 'Florida Panthers', abbr: 'FLA', color: '#C8102E' }, awayTeam: { name: 'Carolina Hurricanes', abbr: 'CAR', color: '#CC0000' }, predictedWinner: 'FLA', actualWinner: 'FLA', confidence: 77, homeWinProb: 65, awayWinProb: 35, projectedScore: '3–2', actualScore: '3–1', correct: true, homeXG: 3.61, awayXG: 2.88 },
  { id: 'proj-005', date: '14 mai 2026', homeTeam: { name: 'Dallas Stars', abbr: 'DAL', color: '#006847' }, awayTeam: { name: 'Winnipeg Jets', abbr: 'WPG', color: '#004C97' }, predictedWinner: 'WPG', actualWinner: 'DAL', confidence: 53, homeWinProb: 47, awayWinProb: 53, projectedScore: '2–3', actualScore: '4–2', correct: false, homeXG: 3.21, awayXG: 3.18 },
  { id: 'proj-006', date: '14 mai 2026', homeTeam: { name: 'Vegas Golden Knights', abbr: 'VGK', color: '#B4975A' }, awayTeam: { name: 'Los Angeles Kings', abbr: 'LAK', color: '#A2AAAD' }, predictedWinner: 'VGK', actualWinner: 'VGK', confidence: 68, homeWinProb: 61, awayWinProb: 39, projectedScore: '3–2', actualScore: '2–1', correct: true, homeXG: 3.41, awayXG: 2.98 },
  { id: 'proj-007', date: '13 mai 2026', homeTeam: { name: 'Toronto Maple Leafs', abbr: 'TOR', color: '#00205B' }, awayTeam: { name: 'Ottawa Senators', abbr: 'OTT', color: '#C2912C' }, predictedWinner: 'TOR', actualWinner: 'TOR', confidence: 71, homeWinProb: 63, awayWinProb: 37, projectedScore: '4–2', actualScore: '3–2', correct: true, homeXG: 3.48, awayXG: 2.94 },
  { id: 'proj-008', date: '13 mai 2026', homeTeam: { name: 'New York Rangers', abbr: 'NYR', color: '#0038A8' }, awayTeam: { name: 'Philadelphia Flyers', abbr: 'PHI', color: '#F74902' }, predictedWinner: 'NYR', actualWinner: 'NYR', confidence: 76, homeWinProb: 67, awayWinProb: 33, projectedScore: '3–1', actualScore: '4–2', correct: true, homeXG: 3.28, awayXG: 2.71 },
  { id: 'proj-009', date: '13 mai 2026', homeTeam: { name: 'Calgary Flames', abbr: 'CGY', color: '#CE1126' }, awayTeam: { name: 'Vancouver Canucks', abbr: 'VAN', color: '#00843D' }, predictedWinner: 'CGY', actualWinner: 'VAN', confidence: 58, homeWinProb: 54, awayWinProb: 46, projectedScore: '3–2', actualScore: '1–3', correct: false, homeXG: 2.98, awayXG: 3.02 },
  { id: 'proj-010', date: '12 mai 2026', homeTeam: { name: 'Boston Bruins', abbr: 'BOS', color: '#FCB514' }, awayTeam: { name: 'Montréal Canadiens', abbr: 'MTL', color: '#AF1E2D' }, predictedWinner: 'BOS', actualWinner: 'BOS', confidence: 82, homeWinProb: 74, awayWinProb: 26, projectedScore: '4–1', actualScore: '5–2', correct: true, homeXG: 3.44, awayXG: 2.81 },
  { id: 'proj-011', date: '12 mai 2026', homeTeam: { name: 'Colorado Avalanche', abbr: 'COL', color: '#6F263D' }, awayTeam: { name: 'Arizona Coyotes', abbr: 'ARI', color: '#8C2633' }, predictedWinner: 'COL', actualWinner: 'COL', confidence: 85, homeWinProb: 78, awayWinProb: 22, projectedScore: '4–1', actualScore: '4–2', correct: true, homeXG: 3.55, awayXG: 2.44 },
  { id: 'proj-012', date: '11 mai 2026', homeTeam: { name: 'Florida Panthers', abbr: 'FLA', color: '#C8102E' }, awayTeam: { name: 'Detroit Red Wings', abbr: 'DET', color: '#CE1126' }, predictedWinner: 'FLA', actualWinner: 'FLA', confidence: 79, homeWinProb: 69, awayWinProb: 31, projectedScore: '3–1', actualScore: '3–2', correct: true, homeXG: 3.61, awayXG: 2.68 },
];

type FilterConf = 'Tous' | 'Correct' | 'Incorrect' | 'Haute';

export default function ProjectionHistoryClient() {
  const [filter, setFilter] = useState<FilterConf>('Tous');
  const [dateFilter, setDateFilter] = useState('');

  const filtered = PROJECTIONS.filter((p) => {
    if (filter === 'Correct') return p.correct === true;
    if (filter === 'Incorrect') return p.correct === false;
    if (filter === 'Haute') return p.confidence >= 75;
    return true;
  }).filter((p) => !dateFilter || p.date.includes(dateFilter));

  const totalCorrect = PROJECTIONS.filter((p) => p.correct === true).length;
  const totalProjections = PROJECTIONS.filter((p) => p.correct !== null).length;
  const accuracy = Math.round((totalCorrect / totalProjections) * 100);
  const highConfCorrect = PROJECTIONS.filter((p) => p.confidence >= 75 && p.correct === true).length;
  const highConfTotal = PROJECTIONS.filter((p) => p.confidence >= 75).length;
  const highConfAccuracy = Math.round((highConfCorrect / highConfTotal) * 100);
  const avgConf = Math.round(PROJECTIONS.reduce((s, p) => s + p.confidence, 0) / PROJECTIONS.length);
  const currentStreak = (() => {
    let streak = 0;
    for (const p of PROJECTIONS) {
      if (p.correct === true) streak++;
      else break;
    }
    return streak;
  })();

  return (
    <div className="space-y-5">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        <div className="card p-4 border-primary/20 bg-primary/5">
          <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground mb-2">Précision Globale</p>
          <p className="text-4xl font-bold font-mono-data text-primary">{accuracy}%</p>
          <p className="text-xs text-muted-foreground mt-1">{totalCorrect}/{totalProjections} projections correctes</p>
        </div>
        <div className="card p-4 border-positive/20 bg-positive-subtle">
          <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground mb-2">Haute Confiance (≥75%)</p>
          <p className="text-4xl font-bold font-mono-data text-positive">{highConfAccuracy}%</p>
          <p className="text-xs text-muted-foreground mt-1">{highConfCorrect}/{highConfTotal} picks haute conf.</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground mb-2">Confiance Moyenne</p>
          <p className="text-4xl font-bold font-mono-data text-foreground">{avgConf}%</p>
          <p className="text-xs text-muted-foreground mt-1">Sur {PROJECTIONS.length} projections</p>
        </div>
        <div className={`card p-4 ${currentStreak >= 3 ? 'border-positive/20 bg-positive-subtle' : ''}`}>
          <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground mb-2">Série en cours</p>
          <p className={`text-4xl font-bold font-mono-data ${currentStreak >= 3 ? 'text-positive' : 'text-foreground'}`}>{currentStreak}</p>
          <p className="text-xs text-muted-foreground mt-1">Projections correctes consécutives</p>
        </div>
      </div>

      {/* Accuracy chart */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Précision — 30 derniers jours</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Évolution quotidienne de la précision du modèle</p>
          </div>
        </div>
        <AccuracyTimelineChart />
      </div>

      {/* Filters + table */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {(['Tous', 'Correct', 'Incorrect', 'Haute'] as FilterConf[]).map((f) => (
              <button
                key={`hist-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
              >
                {f === 'Haute' ? 'Haute Conf.' : f}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Icon name="CalendarDaysIcon" size={14} className="text-muted-foreground" />
            <input
              type="text"
              placeholder="Filtrer par date (ex: 14 mai)"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-input border border-border rounded px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 w-48"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="border-b border-border bg-muted/30">
              <tr>
                <th className="table-header">Date</th>
                <th className="table-header">Domicile</th>
                <th className="table-header">Extérieur</th>
                <th className="table-header">Prédit</th>
                <th className="table-header">Résultat</th>
                <th className="table-header">Confiance</th>
                <th className="table-header">Score Proj.</th>
                <th className="table-header">Score Réel</th>
                <th className="table-header">xGoals</th>
                <th className="table-header">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((proj) => (
                <tr key={proj.id} className="border-b border-border row-hover">
                  <td className="table-cell text-xs text-muted-foreground font-mono-data">{proj.date}</td>
                  <td className="table-cell">
                    <div className="flex items-center gap-1.5">
                      <TeamLogo abbreviation={proj.homeTeam.abbr} color={proj.homeTeam.color} size="sm" />
                      <span className="text-xs font-medium text-foreground font-mono-data">{proj.homeTeam.abbr}</span>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-1.5">
                      <TeamLogo abbreviation={proj.awayTeam.abbr} color={proj.awayTeam.color} size="sm" />
                      <span className="text-xs font-medium text-foreground font-mono-data">{proj.awayTeam.abbr}</span>
                    </div>
                  </td>
                  <td className="table-cell">
                    <span className="text-sm font-bold font-mono-data text-primary">{proj.predictedWinner}</span>
                    <span className="text-2xs text-muted-foreground ml-1">({proj.homeWinProb}%)</span>
                  </td>
                  <td className="table-cell">
                    {proj.actualWinner ? (
                      <span className="text-sm font-bold font-mono-data text-foreground">{proj.actualWinner}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <ConfidenceBadge value={proj.confidence} showLabel={false} />
                  </td>
                  <td className="table-cell font-mono-data text-sm text-muted-foreground">{proj.projectedScore}</td>
                  <td className="table-cell font-mono-data text-sm text-foreground">{proj.actualScore ?? '—'}</td>
                  <td className="table-cell">
                    <span className="text-xs font-mono-data text-muted-foreground">
                      {proj.homeXG} – {proj.awayXG}
                    </span>
                  </td>
                  <td className="table-cell">
                    {proj.correct === null ? (
                      <span className="status-badge bg-muted text-muted-foreground border border-border text-2xs">En attente</span>
                    ) : proj.correct ? (
                      <span className="status-badge bg-positive-subtle text-positive border border-positive/20 text-2xs flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={11} />
                        Correct
                      </span>
                    ) : (
                      <span className="status-badge bg-negative-subtle text-negative border border-negative/20 text-2xs flex items-center gap-1">
                        <Icon name="XCircleIcon" size={11} />
                        Incorrect
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
          <span>{filtered.length} projection{filtered.length !== 1 ? 's' : ''} affichée{filtered.length !== 1 ? 's' : ''}</span>
          <span className="font-mono-data">
            {filtered.filter((p) => p.correct === true).length} correctes ·{' '}
            {filtered.filter((p) => p.correct === false).length} incorrectes
          </span>
        </div>
      </div>
    </div>
  );
}